import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Maximize2 } from "lucide-react";
import { formatMoney } from "@mavatech/shared";
import { trpc } from "@/lib/trpc";
import { keepPreviousData } from "@tanstack/react-query";
import type { PickedArticle } from "./ArticlePicker";
import {
  SupplierChips,
  SupplierTags,
  shownPrices,
  useDebounced,
  useSupplierFilter,
} from "./SupplierFilter";

const SIZE_KEY = "mavatech_article_dropdown_size";

/**
 * Beschreibungsfeld einer Position mit eingebauter Artikelsuche: ab zwei
 * Zeichen erscheint eine Trefferliste (Name, Nummer, Nettopreis, Einkaufspreis)
 * direkt unter dem Feld — ohne den Umweg über den separaten Artikel-Dialog.
 */
export function DescriptionField({
  value,
  onChange,
  onPick,
  onExpandSearch,
  className,
}: {
  value: string;
  onChange: (text: string) => void;
  onPick: (a: PickedArticle) => void;
  /** Öffnet die große Artikelsuche (Dialog) mit dem aktuellen Suchtext vorbelegt — für „hier ist
   * die kleine Trefferliste zu knapp, ich will richtig durchsuchen/vergleichen". */
  onExpandSearch?: (search: string) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const ddRef = useRef<HTMLDivElement>(null);
  // Die Trefferliste wird per Portal an <body> gehängt und mit fixer Position unter das Feld gesetzt —
  // sonst würde sie vom scrollbaren Positionen-Bereich (overflow-x-auto) abgeschnitten und wäre unsichtbar.
  const [pos, setPos] = useState<{
    left: number;
    top: number;
    maxH: number;
    h: number;
    above: boolean;
  } | null>(null);
  // Vom Benutzer per Ziehen eingestellte Größe der Trefferliste (wird gemerkt)
  const [userSize, setUserSize] = useState<{ w: number; h: number } | null>(
    () => {
      try {
        const v = JSON.parse(localStorage.getItem(SIZE_KEY) ?? "null");
        return v && v.w > 0 && v.h > 0 ? v : null;
      } catch {
        return null;
      }
    },
  );
  const sizeRef = useRef(userSize);
  sizeRef.current = userSize;
  const q = value.trim();
  const [supplierId, setSupplierId] = useSupplierFilter();
  // Suche erst kurz nach dem Tippen starten; die alte Trefferliste bleibt solange stehen (kein Flackern "Keine Treffer")
  const dq = useDebounced(q, 250);
  const enabled = open && q.length >= 2;
  const list = trpc.article.list.useQuery(
    { search: dq, supplierId: supplierId || undefined },
    {
      enabled: open && dq.length >= 2,
      placeholderData: keepPreviousData,
    },
  );
  const settling = q !== dq || list.isFetching;

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const el = boxRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const below = window.innerHeight - r.bottom - 12;
      const above = r.top - 12;
      const useAbove = below < 220 && above > below;
      const maxH = Math.max(160, Math.min(384, useAbove ? above : below));
      const us = sizeRef.current;
      const width = Math.min(us?.w ?? 580, window.innerWidth - 16);
      const h = us
        ? Math.max(
            160,
            Math.min(
              us.h,
              useAbove ? above : window.innerHeight - r.bottom - 8,
            ),
          )
        : maxH;
      const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
      setPos({
        left,
        top: useAbove ? r.top - h - 4 : r.bottom + 4,
        maxH,
        h,
        above: useAbove,
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, userSize]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      const t = e.target as Node;
      if (boxRef.current?.contains(t) || ddRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const allResults = list.data ?? [];
  const rows = allResults.slice(0, 25);
  const moreCount = allResults.length - rows.length;
  const anyMulti = rows.some(
    (a) => a.prices.filter((p) => p.supplier).length > 1,
  );

  return (
    <div className="relative" ref={boxRef}>
      <input
        className={className}
        placeholder="Leistungsbeschreibung — tippen sucht im Artikelstamm"
        value={value}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
      />
      {enabled &&
        pos &&
        createPortal(
          <div
            ref={ddRef}
            style={{
              position: "fixed",
              left: pos.left,
              top: pos.top,
              width: Math.min(userSize?.w ?? 580, window.innerWidth - 16),
              ...(userSize ? { height: pos.h } : { maxHeight: pos.maxH }),
            }}
            className={`z-[60] flex flex-col bg-bg-card border border-border rounded shadow-lg transition-opacity ${settling ? "opacity-80" : ""}`}
          >
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className="sticky top-0 z-10 bg-bg-card border-b border-border-subtle px-3 py-1.5 flex items-center justify-between gap-2">
                <SupplierChips value={supplierId} onChange={setSupplierId} />
                {list.isError ? (
                  <span className="text-2xs text-danger">
                    Suche zu aufwendig – bitte genauer eingeben (ein Wort dazu)
                  </span>
                ) : (
                  settling && (
                    <span className="text-2xs text-text-tertiary">sucht …</span>
                  )
                )}
              </div>
              {!rows.length ? (
                <p className="text-sm text-text-tertiary p-3">
                  {list.isError
                    ? "Die Suche war zu aufwendig. Bitte genauer eingeben, z. B. ein Wort dazuschreiben (Kugelhahn 25)."
                    : settling || list.data === undefined
                      ? "Suche läuft …"
                      : "Keine Treffer."}
                </p>
              ) : (
                rows.map((a) => {
                  const shown = shownPrices(
                    a.prices,
                    supplierId || undefined,
                    a.bestPurchasePriceCents,
                  );
                  const listNet = shown.list;
                  return (
                    <button
                      key={a.id}
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-bg-hover flex items-center justify-between gap-3"
                      onClick={() => {
                        onPick({
                          id: a.id,
                          name: a.name,
                          unit: a.unit,
                          salePriceCents: a.salePriceCents,
                          purchasePriceCents: shown.purchase,
                        });
                        setOpen(false);
                      }}
                    >
                      <span className="min-w-0">
                        <span className="block text-text-primary truncate">
                          {a.name}
                        </span>
                        <span className="block text-2xs text-text-tertiary">
                          {a.ownNumber ?? a.category ?? ""}
                        </span>
                      </span>
                      <span className="flex items-center gap-2 flex-shrink-0 text-2xs text-text-tertiary text-right">
                        <span>
                          {listNet != null ? formatMoney(listNet) : "—"} netto
                        </span>
                        <span>
                          {shown.purchase != null
                            ? formatMoney(shown.purchase)
                            : "—"}{" "}
                          EK
                        </span>
                        <SupplierTags
                          prices={a.prices}
                          supplierId={supplierId}
                          stacked
                          wide={anyMulti}
                        />
                      </span>
                    </button>
                  );
                })
              )}
            </div>
            {onExpandSearch && (
              <button
                type="button"
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs text-accent hover:bg-bg-hover border-t border-border-subtle flex-shrink-0 bg-bg-card"
                onClick={() => {
                  onExpandSearch(q);
                  setOpen(false);
                }}
              >
                <Maximize2 className="w-3 h-3" />
                {moreCount > 0
                  ? `${moreCount}+ weitere Treffer — groß suchen`
                  : "Groß suchen / Favoriten durchsuchen"}
              </button>
            )}
            <div
              title="Ziehen, um die Größe zu ändern (Doppelklick: zurücksetzen)"
              onDoubleClick={() => {
                setUserSize(null);
                try {
                  localStorage.removeItem(SIZE_KEY);
                } catch {
                  /* egal */
                }
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                const el = ddRef.current;
                if (!el) return;
                const rect = el.getBoundingClientRect();
                const startX = e.clientX;
                const startY = e.clientY;
                const startW = rect.width;
                const startH = rect.height;
                const above = pos.above;
                const onMove = (ev: MouseEvent) => {
                  const w = Math.max(
                    320,
                    Math.min(
                      window.innerWidth - rect.left - 8,
                      startW + (ev.clientX - startX),
                    ),
                  );
                  // oberhalb des Feldes wächst die Liste nach oben → Ziehrichtung umkehren
                  const dy = above ? startY - ev.clientY : ev.clientY - startY;
                  const h = Math.max(
                    160,
                    Math.min(window.innerHeight - 24, startH + dy),
                  );
                  setUserSize({ w: Math.round(w), h: Math.round(h) });
                };
                const onUp = () => {
                  document.removeEventListener("mousemove", onMove);
                  document.removeEventListener("mouseup", onUp);
                  try {
                    if (sizeRef.current)
                      localStorage.setItem(
                        SIZE_KEY,
                        JSON.stringify(sizeRef.current),
                      );
                  } catch {
                    /* egal */
                  }
                };
                document.addEventListener("mousemove", onMove);
                document.addEventListener("mouseup", onUp);
              }}
              className={`absolute right-0 ${pos.above ? "top-0" : "bottom-0"} z-20 w-5 h-5 cursor-nwse-resize flex items-end justify-end p-0.5 text-text-tertiary hover:text-accent select-none bg-bg-card/80 rounded`}
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.2"
                className={pos.above ? "rotate-90" : ""}
              >
                <path d="M9 1L1 9M9 5L5 9" />
              </svg>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
